import { useState, useCallback, type ReactNode } from 'react';

/**
 * The numeric answer pad for suit-count and max-count questions (feature 028).
 *
 * Restyled onto the 023 table-session idiom — same digits-only rule, same Enter-to-submit, same
 * trimmed value handed up. The behaviour is deliberately untouched: the verdict is a trimmed
 * string comparison against the hand's stored answer, so anything this component does to the
 * value changes what counts as correct.
 *
 * Sizing follows the constitution's legibility floors rather than the pre-028 clamps: nothing
 * below 12 CSS px, and the button keeps a hit area well over 24 x 24 at every viewport.
 */

interface SuitCountInputProps {
  onSubmit: (answer: string) => void;
  disabled: boolean;
  /**
   * A second action rendered in Submit's own row (feature 028: the page's "Skip Hand"). The row
   * is a column everywhere but phones, so a caller that passes nothing — and every caller that
   * did before — gets exactly the button it had. See `.counting-answer-actions` in index.css.
   */
  trailingAction?: ReactNode;
}

// xnarrow/padswap trim the padding, never the font size — height is found in the box, not in
// the type. Mirrors YesNoPad so the two pads sit at the same rhythm inside the call panel.
const BTN =
  'w-full rounded-lg py-3 text-center text-[15px] font-bold tracking-wide transition-colors ' +
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 ' +
  'disabled:cursor-not-allowed disabled:opacity-60 xnarrow:py-2 padswap:py-2';

export function SuitCountInput({ onSubmit, disabled, trailingAction }: SuitCountInputProps) {
  const [value, setValue] = useState('');

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;
    if (input === '' || /^\d+$/.test(input)) {
      setValue(input);
    }
  }, []);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
      if (e.key === 'Enter' && value.trim()) {
        onSubmit(value.trim());
      }
      return;
    }
    if (!/^\d$/.test(e.key)) {
      e.preventDefault();
    }
  }, [value, onSubmit]);

  const handleSubmit = useCallback(() => {
    if (value.trim()) {
      onSubmit(value.trim());
    }
  }, [value, onSubmit]);

  return (
    <div className="space-y-3 xnarrow:space-y-2 padswap:space-y-2" data-testid="suit-count-input">
      <div className="flex justify-center">
        <input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          placeholder="0"
          aria-label="Your answer"
          // 16px minimum on the input itself: iOS Safari zooms the page when a focused input
          // is smaller, and the pre-028 page carried a scroll-restore workaround for exactly
          // that. Sizing it correctly removes the need for the workaround.
          className="w-16 rounded-lg border border-line-strong bg-card px-3 py-2 text-center
            text-[18px] font-semibold text-ink
            focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-600
            disabled:bg-cream disabled:text-ink-soft"
          autoFocus
        />
      </div>
      <div className="counting-answer-actions">
        <button
          type="button"
          onClick={handleSubmit}
          disabled={disabled || !value.trim()}
          data-testid="counting-submit"
          className={`${BTN} bg-primary-600 text-white shadow-[0_2px_0_#063d21] hover:bg-primary-700 focus-visible:ring-primary-600`}
        >
          Submit
        </button>
        {trailingAction}
      </div>
    </div>
  );
}
